import { useState, useEffect, useCallback, useRef } from 'react';
import { getSheetsSettings, updateSheetsSettings } from '../api/sheetsSyncApi';
import type { SheetsSettings, SpreadsheetRef } from '../types';
import { ApiError, ApiErrorCode } from '../../../services/api/errors/api-error';

type SettingsState =
  | { status: 'loading' }
  | { status: 'ready'; settings: SheetsSettings; dirty: boolean }
  | { status: 'saving'; settings: SheetsSettings }
  | { status: 'save-error'; settings: SheetsSettings; message: string }
  | { status: 'load-error'; message: string };

export interface UseSheetsSettingsReturn {
  state: SettingsState;
  setSpreadsheets: (v: SpreadsheetRef[]) => void;
  setTabNameFormat: (v: string) => void;
  setStatusMap: (v: Record<string, string>) => void;
  save: () => Promise<void>;
  reload: () => void;
}

export function useSheetsSettings(): UseSheetsSettingsReturn {
  const [state, setState] = useState<SettingsState>({ status: 'loading' });
  const settingsRef = useRef<SheetsSettings | null>(null);

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const settings = await getSheetsSettings();
      settingsRef.current = settings;
      setState({ status: 'ready', settings, dirty: false });
    } catch (err) {
      if (err instanceof ApiError && err.code === ApiErrorCode.NOT_FOUND) {
        const defaults: SheetsSettings = {
          spreadsheets: [],
          tab_name_format: 'MMMM YYYY',
          status_map: {},
        };
        settingsRef.current = defaults;
        setState({ status: 'ready', settings: defaults, dirty: false });
        return;
      }
      const message =
        err instanceof ApiError ? err.getUserMessage() : 'Failed to load settings.';
      setState({ status: 'load-error', message });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const mutate = useCallback((updater: (s: SheetsSettings) => SheetsSettings) => {
    setState((prev) => {
      if (prev.status !== 'ready' && prev.status !== 'save-error') return prev;
      const next = updater(prev.settings);
      settingsRef.current = next;
      return { status: 'ready', settings: next, dirty: true };
    });
  }, []);

  const setSpreadsheets = useCallback(
    (spreadsheets: SpreadsheetRef[]) => mutate((s) => ({ ...s, spreadsheets })),
    [mutate],
  );

  const setTabNameFormat = useCallback(
    (tab_name_format: string) => mutate((s) => ({ ...s, tab_name_format })),
    [mutate],
  );

  const setStatusMap = useCallback(
    (status_map: Record<string, string>) => mutate((s) => ({ ...s, status_map })),
    [mutate],
  );

  const save = useCallback(async () => {
    const settings = settingsRef.current;
    if (!settings) return;
    setState({ status: 'saving', settings });
    try {
      await updateSheetsSettings(settings);
      setState({ status: 'ready', settings, dirty: false });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.getUserMessage() : 'Failed to save settings.';
      setState({ status: 'save-error', settings, message });
    }
  }, []);

  return { state, setSpreadsheets, setTabNameFormat, setStatusMap, save, reload: load };
}
